import {
    Box,
    Flex,
    Heading,
    Tag,
    Text,
    useColorModeValue,
} from '@chakra-ui/react';
import { useMemo, useState } from 'react';
import { MdMap } from 'react-icons/md';
import Card from 'components/card/Card';
import { typography } from 'theme/tokens';
import { translate } from '../../i18n';
import ErrorState from '../../components/states/ErrorState';
import EmptyState from '../../components/states/EmptyState';
import SkeletonCardGrid from '../../components/states/SkeletonCardGrid';
import { RoadmapItemDto } from '../../models/roadmap/dto/roadmap.dto';
import { useSelectedOrganization } from '../organization/hooks/useSelectedOrganization';
import { classifyRead } from '../_shared/readState';
import { useGetRoadmap } from './hooks/useGetRoadmap';
import RoadmapBoard from './components/RoadmapBoard';

export default function RoadmapPage() {
    const { selectedOrganization } = useSelectedOrganization();
    // `useGetRoadmap` is `enabled: Boolean(organizationId)`, and this page
    // already failed SAFE: its `!selectedOrganization` early return sat above
    // the loading arm and rendered a real select-an-organization prompt. What
    // it did was re-derive the hook's gate from the STORE, in a second file —
    // D62's fragile shape: give the hook a second `enabled:` conjunct and the
    // store guard still passes while the query stays disabled, so the raw
    // `isLoading` below would spin forever. Classifying on the query's own
    // flags cannot drift, because `data` and `isInitialLoading` are facts the
    // query knows about itself.
    const { data, isInitialLoading, isError, refetch } = useGetRoadmap(
        selectedOrganization?.id,
    );

    const textColor = useColorModeValue('secondaryGray.900', 'white');
    const mutedColor = useColorModeValue('secondaryGray.600', 'secondaryGray.500');

    const [selectedLabels, setSelectedLabels] = useState<string[]>([]);

    // Union of all labels across shared + client items, for the filter chips.
    const availableLabels = useMemo(() => {
        const set = new Set<string>();
        [...(data?.shared ?? []), ...(data?.clientSpecific ?? [])].forEach((item) =>
            item.labels.forEach((label) => set.add(label)),
        );
        return Array.from(set).sort();
    }, [data]);

    const filterItems = (items: RoadmapItemDto[]) =>
        selectedLabels.length === 0
            ? items
            : items.filter((item) =>
                  item.labels.some((label) => selectedLabels.includes(label)),
              );

    const toggleLabel = (label: string) =>
        setSelectedLabels((prev) =>
            prev.includes(label)
                ? prev.filter((l) => l !== label)
                : [...prev, label],
        );

    const filteredClient = filterItems(data?.clientSpecific ?? []);
    const filteredShared = filterItems(data?.shared ?? []);
    const hasClientItems = filteredClient.length > 0;

    const readState = classifyRead(isError, isInitialLoading, data);

    // Deliberately NOT `ScopePrompt`, which is what the other pages in this
    // class render: this page already owns a dedicated prompt with a
    // description, translated in both catalogues (`roadmap.select_org_title` /
    // `.select_org_desc`), and `ScopePrompt` would replace it with the generic
    // borrowed `command_palette.select_org_first` and lose the second line.
    // The gate in `list-read-state-gate.test.ts` is satisfied by the
    // `'never-issued'` classification, not by the component identity.
    if (readState === 'never-issued') {
        return (
            <Card>
                <EmptyState
                    icon={MdMap}
                    title={translate('roadmap.select_org_title')}
                    description={translate('roadmap.select_org_desc')}
                />
            </Card>
        );
    }

    return (
        <Card flexDirection="column" w="100%">
            <Flex px="25px" mb="8px" direction="column" gap="2px">
                <Heading as="h2" color={textColor} {...typography.pageTitle}>
                    {translate('roadmap.title')}
                </Heading>
                <Text color={mutedColor} fontSize="sm">
                    {translate('roadmap.subtitle')}
                </Text>
            </Flex>

            <Box px="25px" py="12px">
                {readState === 'loading' ? (
                    <SkeletonCardGrid />
                ) : readState === 'unreadable' ? (
                    // `unreadable`, not the raw `isError`: a BACKGROUND
                    // refetch that fails over a board already on screen keeps
                    // the board (R96-3). Reading `isError` alone would wipe a
                    // correct roadmap because a 30s refresh 403'd.
                    <ErrorState
                        message={translate('roadmap.error_loading')}
                        onRetry={refetch}
                    />
                ) : (
                    <Flex direction="column" gap="24px">
                        {/* Label filter */}
                        {availableLabels.length > 0 && (
                            <Flex align="center" gap={2} wrap="wrap">
                                <Text
                                    color={mutedColor}
                                    fontSize="xs"
                                    fontWeight="600"
                                    mr={1}
                                >
                                    {translate('roadmap.filter_by_label')}
                                </Text>
                                {availableLabels.map((label) => {
                                    const active = selectedLabels.includes(label);
                                    return (
                                        <Tag
                                            key={label}
                                            size="sm"
                                            borderRadius="full"
                                            cursor="pointer"
                                            variant={active ? 'solid' : 'outline'}
                                            colorScheme={active ? 'brand' : 'gray'}
                                            transition="all 0.15s"
                                            onClick={() => toggleLabel(label)}
                                        >
                                            {label}
                                        </Tag>
                                    );
                                })}
                                {selectedLabels.length > 0 && (
                                    <Text
                                        as="button"
                                        color="brand.500"
                                        fontSize="xs"
                                        fontWeight="600"
                                        ml={1}
                                        onClick={() => setSelectedLabels([])}
                                    >
                                        {translate('roadmap.filter_clear')}
                                    </Text>
                                )}
                            </Flex>
                        )}

                        {/* This tenant's own requests — only shown when they have any */}
                        {hasClientItems && (
                            <Box>
                                <Heading as="h3" color={textColor} fontSize="lg" mb="2px">
                                    {translate('roadmap.client_title')}
                                </Heading>
                                <Text color={mutedColor} fontSize="sm" mb="16px">
                                    {translate('roadmap.client_subtitle')}
                                </Text>
                                <RoadmapBoard items={filteredClient} />
                            </Box>
                        )}

                        {/* Shared product roadmap */}
                        <Box>
                            <Heading as="h3" color={textColor} fontSize="lg" mb="2px">
                                {translate('roadmap.shared_title')}
                            </Heading>
                            <Text color={mutedColor} fontSize="sm" mb="16px">
                                {translate('roadmap.shared_subtitle')}
                            </Text>
                            {filteredShared.length > 0 ? (
                                <RoadmapBoard items={filteredShared} />
                            ) : (
                                <EmptyState
                                    icon={MdMap}
                                    title={translate('roadmap.empty_shared')}
                                />
                            )}
                        </Box>

                        {data?.fetchedAt && (
                            <Text color={mutedColor} fontSize="xs">
                                {translate('roadmap.updated_at', {
                                    date: new Date(data.fetchedAt).toLocaleDateString('fr-FR'),
                                })}
                            </Text>
                        )}
                    </Flex>
                )}
            </Box>
        </Card>
    );
}
